import type { IServerSideDatasource } from "ag-grid-community";
import dayjs from "dayjs";
import React from "react";

import { trpc } from "@blis/trpc/react";

import { AgGridReact } from "@/components/grid/AgGrid";
import { Link } from "@/components/Link";
import { StatusBadge } from "@/components/ui/icons";
import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";

export function TestResultsGrid() {
  const utils = trpc.useUtils();

  const serverSide: IServerSideDatasource = React.useMemo(
    () => ({
      getRows: async (params) => {
        const { startRow, endRow, filterModel, sortModel } = params.request;
        // console.log({ startRow, endRow, filterModel });
        try {
          const { data, totalCount } = await utils.browse.testResults.fetch({
            take: (endRow ?? 0) - (startRow ?? 0),
            skip: startRow ?? 0,
            // biome-ignore lint/suspicious/noExplicitAny: TODO
            filterModel: filterModel as any,
            // biome-ignore lint/suspicious/noExplicitAny: TODO
            sortModel: sortModel as any,
          });
          params.success({
            rowData: data,
            rowCount: totalCount,
          });
        } catch (_err) {
          params.fail();
          return;
        }
      },
    }),
    [utils],
  );
  const { gridProps } = useAdminServerSideGrid<Record<string, unknown>>({
    serverSideDatasource: serverSide,
    paginationPageSize: 100,
    paginationPageSizeSelector: [10, 25, 50, 100, 500, 1000, 5000],
  });

  return (
    <div className="ag-theme-quartz ag-theme-blis ag-theme-blis-full h-full w-full">
      <AgGridReact
        {...gridProps}
        // rowData={results}
        // animateRows
        defaultColDef={{
          sortable: false,
          filterParams: {
            maxNumConditions: 1,
          },
        }}
        columnDefs={[
          {
            field: "date",
            filter: "agDateColumnFilter",
            filterParams: {
              filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
            },
            sortable: true,
            initialSort: "desc",
            valueFormatter: ({ value }) =>
              dayjs(value).format("YYYY-MM-DD HH:mm"),
          },

          {
            field: "accessionId",
            headerName: "Accession",
            width: 140,
            filter: "agTextColumnFilter",
            sortable: true,
            cellRenderer: AccessionLink,
          },
          {
            field: "accessionStatus",
            headerName: "Status",
            filter: "agSetColumnFilter",
            filterParams: {
              values: [
                "new",
                "received",
                "in_progress",
                "completed",
                "final",
                "cancelled",
              ],
            },
            width: 120,
            minWidth: 120,
            valueFormatter: ({ value }) => {
              return value?.toUpperCase();
            },
            cellRenderer: AccessionStatus,
          },
          {
            field: "org",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const orgs = await utils.orgs.list.fetch();
                params.success(orgs.map((org) => org.name));
              },
            },
          },
          {
            field: "caseId",
            filter: "agTextColumnFilter",
          },
          {
            field: "unosId",
            filter: "agTextColumnFilter",
          },

          {
            field: "testSpec",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const testSpecs = await utils.tests.all.fetch();
                params.success(testSpecs.map((testSpec) => testSpec.name));
              },
              excelMode: "windows",
            },
          },
          {
            field: "testResultSpec",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const testSpecs = await utils.tests.resultSpecs.fetch();
                params.success(testSpecs.map((testSpec) => testSpec.name));
              },
              excelMode: "windows",
            },
          },
          {
            field: "isReflex",
            filter: "agSetColumnFilter",
            filterParams: { values: ["true", "false"] },
          },
          {
            field: "testType",
            filter: "agSetColumnFilter",
            filterParams: {
              values: ["SEROLOGY", "CLINICAL", "HLA"],
            },
            initialHide: true,
          },
          {
            field: "instrument",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const instruments = await utils.instruments.list.fetch();
                params.success(
                  instruments.map((instrument) => instrument.model),
                );
              },
            },
            initialHide: true,
          },
          { field: "initialResult", filter: false, initialHide: true },
          {
            field: "initialResultEnum",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const testResults = await utils.results.testResults.fetch();
                params.success(
                  testResults.map((testResult) => testResult.description),
                );
              },
            },
            initialHide: true,
          },
          { field: "initialResultNumeric", filter: false, initialHide: true },
          { field: "finalResult", filter: false },
          {
            field: "finalResultEnum",
            filter: "agSetColumnFilter",
            filterParams: {
              values: async (params) => {
                const testResults = await utils.results.testResults.fetch();
                params.success(
                  testResults.map((testResult) => testResult.description),
                );
              },
            },
            initialHide: true,
          },
          {
            field: "finalResultNumeric",
            filter: "agNumberColumnFilter",
            initialHide: true,
          },
        ]}
        sideBar={{
          toolPanels: ["columns", "filters"],
        }}
      />
    </div>
  );
}

const AccessionLink = React.memo(({ value }: { value: string }) => (
  <Link to="/accessions/$id" params={{ id: value }}>
    {value}
  </Link>
));

const AccessionStatus = React.memo(({ value }: { value: string }) => (
  <div className="flex h-full items-center">
    <StatusBadge status={value as "new"} />
  </div>
));
